/**
 * KoolKonnectMagicPage — the new default KoolKonnect experience.
 *
 * Design intent (2026-07-22 directive):
 *   Match Adobe Firefly / Canva Magic / Ideogram / Krea UX:
 *   ONE upload + ONE prompt + N output tiles → parallel generation → gallery.
 *
 * Flow:
 *   1. Drop a photo (optional for pure text-to-image)
 *   2. Describe what you want in plain English
 *   3. Pick as many output tiles as you like (or "Everything" to select all)
 *   4. Click Generate → images appear immediately, videos/music stream in via
 *      polling every 4 seconds until complete.
 *   5. Download / re-run / share each output.
 *
 * Every AI call is routed through /api/koolkonnect/magic/* which itself
 * routes through the Kaharian Gateway. No direct provider calls.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import {
  Upload, Sparkles, Loader2, Download, RefreshCw, Wand2, Image as ImageIcon,
  Smartphone, Monitor, Printer, Package, Sun, User as UserIcon, Hexagon,
  Video, Music, Trash2, Check, X, ArrowRight, ChevronLeft, FileText,
  Mic, BookOpen, PenTool, Clapperboard, Zap, FolderOpen, Film, HardDrive,
} from 'lucide-react';
import GenerationProgress from '../components/koolkonnect/GenerationProgress';

const API = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  instagram: ImageIcon,
  smartphone: Smartphone,
  monitor: Monitor,
  printer: Printer,
  package: Package,
  sun: Sun,
  user: UserIcon,
  hexagon: Hexagon,
  video: Video,
  'smartphone-video': Smartphone,
  music: Music,
  mic: Mic,
  book: BookOpen,
  pen: PenTool,
  clapper: Clapperboard,
  wand: Wand2,
};

// Brand-colored dots keep the tiles small; real logos are inline SVGs below.
const SOCIAL_LOGOS = {
  instagram: {
    color: 'from-pink-500 via-red-500 to-yellow-500',
    label: 'Instagram',
  },
  facebook: { color: 'bg-[#1877F2]', label: 'Facebook' },
  linkedin: { color: 'bg-[#0A66C2]', label: 'LinkedIn' },
  tiktok:   { color: 'bg-[#000000]', label: 'TikTok' },
  youtube:  { color: 'bg-[#FF0000]', label: 'YouTube' },
  pinterest:{ color: 'bg-[#E60023]', label: 'Pinterest' },
  spotify:  { color: 'bg-[#1DB954]', label: 'Spotify' },
};

function SocialDots({ socials }) {
  if (!socials || socials.length === 0) return null;
  return (
    <div className="flex -space-x-1.5">
      {socials.slice(0, 4).map(s => {
        const meta = SOCIAL_LOGOS[s];
        if (!meta) return null;
        const bgClass = meta.color.startsWith('from-')
          ? `bg-gradient-to-br ${meta.color}`
          : meta.color;
        return (
          <div
            key={s}
            title={meta.label}
            className={`h-5 w-5 rounded-full border-[1.5px] border-black/40 ${bgClass} shadow`}
          />
        );
      })}
    </div>
  );
}

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

// ─── Preset tile with rotating Ken-Burns covers ───────────────────────────
function PresetTile({ preset, index, isOn, onToggle }) {
  const Icon = ICONS[preset.icon] || ImageIcon;
  const covers = preset.covers || [];
  const [coverIdx, setCoverIdx] = useState(0);

  // Rotate the 3 covers every 9 seconds — staggered per tile so they never
  // pulse together (looks alive, not synchronized like a broken slideshow).
  useEffect(() => {
    if (covers.length <= 1) return;
    const stagger = (index % 5) * 900;
    const t0 = setTimeout(() => {
      const interval = setInterval(() => {
        setCoverIdx((i) => (i + 1) % covers.length);
      }, 9000);
      window.__mgtiles = window.__mgtiles || {};
      window.__mgtiles[preset.id] = interval;
    }, stagger);
    return () => {
      clearTimeout(t0);
      if (window.__mgtiles?.[preset.id]) {
        clearInterval(window.__mgtiles[preset.id]);
        delete window.__mgtiles[preset.id];
      }
    };
  }, [covers.length, index, preset.id]);

  const activeCover = covers[coverIdx];

  return (
    <button
      onClick={onToggle}
      className={`group relative aspect-[4/3] rounded-2xl border-2 transition-all overflow-hidden text-left ${
        isOn
          ? 'border-emerald-400 shadow-lg shadow-emerald-500/30 ring-2 ring-emerald-400/20'
          : 'border-white/10 hover:border-white/30'
      }`}
      data-testid={`magic-preset-${preset.id}`}
    >
      {/* Cover carousel — Ken-Burns slow zoom, soft 2.5s cross-fade between covers */}
      <div className="absolute inset-0 bg-black">
        {covers.map((c, i) => (
          <img
            key={c}
            src={c}
            alt=""
            className={`absolute inset-0 w-full h-full object-cover ${
              i === coverIdx ? 'opacity-100 kenburns-active' : 'opacity-0'
            }`}
            style={{
              // Long ease-in-out crossfade so two covers overlap visibly at the
              // midpoint — no jarring hard switches, just a dreamy dissolve.
              transition: 'opacity 2500ms cubic-bezier(0.4, 0.0, 0.2, 1)',
            }}
            loading="lazy"
            draggable="false"
          />
        ))}
        {/* Dark scrim so text stays legible */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/10" />
      </div>

      {/* Icon + selection indicator */}
      <div className="relative h-full flex flex-col p-3 z-10">
        <div className="flex items-start justify-between">
          <div className={`h-9 w-9 rounded-xl grid place-items-center backdrop-blur-md ${isOn ? 'bg-emerald-400 text-black' : 'bg-black/60 text-white/90 border border-white/10'}`}>
            <Icon className="h-4 w-4" />
          </div>
          {isOn ? (
            <div className="h-5 w-5 rounded-full bg-emerald-400 grid place-items-center shadow">
              <Check className="h-3 w-3 text-black" />
            </div>
          ) : (
            <SocialDots socials={preset.socials} />
          )}
        </div>
        <div className="mt-auto">
          <div className="text-sm font-semibold text-white drop-shadow-lg">{preset.label}</div>
          <div className="text-[10px] text-white/85 mt-0.5 drop-shadow flex items-center gap-1">
            {preset.kind === 'image' && preset.aspect}
            {preset.kind !== 'image' && preset.kind.toUpperCase()}
            <span className="text-white/50">·</span>
            <span>{preset.credits}cr</span>
          </div>
        </div>
      </div>
    </button>
  );
}

export default function KoolKonnectMagicPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [presets, setPresets] = useState([]);
  const [selected, setSelected] = useState(new Set(['instagram_square', 'story_reel', 'facebook_ad']));
  const [prompt, setPrompt] = useState('');
  const [brandHint, setBrandHint] = useState('');
  const [refUrls, setRefUrls] = useState([]);
  const [refFiles, setRefFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [identityMode, setIdentityMode] = useState('preserve'); // 'preserve' | 'restyle'
  const [generating, setGenerating] = useState(false);
  const [session, setSession] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [trayOpen, setTrayOpen] = useState(false);
  const pollTimerRef = useRef(null);

  // UGC composer state — opens when the "UGC Video Ad Recipe" tile is generated.
  const [ugcOpen, setUgcOpen] = useState(false);
  const [ugcOrient, setUgcOrient] = useState('vertical'); // 'vertical' | 'horizontal'
  const [ugcAutoCaps, setUgcAutoCaps] = useState(true);
  const [ugcCaptions, setUgcCaptions] = useState(['', '', '', '']);
  const [ugcSubmitting, setUgcSubmitting] = useState(false);

  // Google Drive status pill in the header.
  const [driveStatus, setDriveStatus] = useState({ connected: false, configured: true });

  // Kaharian Hub version — live-updates every 60s. Powers the footer pill.
  const [hubVersion, setHubVersion] = useState('');

  // Direct Edit composer state — appears when the "Direct Edit" tile is picked.
  const [directOpen, setDirectOpen] = useState(false);
  const [directIntent, setDirectIntent] = useState('edit'); // 'edit' | 'compose'
  const [directAspect, setDirectAspect] = useState('1:1');
  const [directSubmitting, setDirectSubmitting] = useState(false);

  // Load presets + last few sessions + Drive status + Kaharian Hub version.
  useEffect(() => {
    (async () => {
      try {
        const [pRes, hRes, dRes, hubRes] = await Promise.all([
          axios.get(`${API}/api/koolkonnect/magic/presets`, auth()),
          axios.get(`${API}/api/koolkonnect/magic/sessions?limit=6`, auth()),
          axios.get(`${API}/api/drive/status`, auth()).catch(() => ({ data: { connected: false, configured: true } })),
          axios.get(`${API}/api/koolkonnect/hub/version`, auth()).catch(() => ({ data: { version: '' } })),
        ]);
        setPresets(pRes.data.presets || []);
        setHistory(hRes.data || []);
        setDriveStatus(dRes.data || { connected: false, configured: true });
        setHubVersion(hubRes.data?.version || '');
      } catch (e) {
        // Silent — user still sees the page.
      }
    })();
    // Refresh Hub version every 60s so upgrades appear live without reload.
    const iv = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/api/koolkonnect/hub/version`, auth());
        if (data?.version) setHubVersion(data.version);
      } catch (_e) { /* silent */ }
    }, 60000);
    return () => clearInterval(iv);
  }, []);

  const connectDrive = async () => {
    try {
      const { data } = await axios.get(`${API}/api/drive/connect`, auth());
      if (data?.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        toast.error('Could not open Drive consent screen');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Drive connect failed');
    }
  };

  // Poll while any output is still processing.
  useEffect(() => {
    if (!session?.id) return;
    const anyProcessing = (session.outputs || []).some(o => o.status === 'processing');
    if (!anyProcessing) return;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/api/koolkonnect/magic/session/${session.id}`, auth());
        setSession(data);
        const stillProcessing = (data.outputs || []).some(o => o.status === 'processing');
        if (stillProcessing) pollTimerRef.current = setTimeout(tick, 4000);
      } catch (_e) {
        pollTimerRef.current = setTimeout(tick, 8000);
      }
    };
    pollTimerRef.current = setTimeout(tick, 4000);
    return () => { if (pollTimerRef.current) clearTimeout(pollTimerRef.current); };
  }, [session]);

  // 2026-07-26 — Protect in-flight generations from accidental navigation.
  // Browser-native `beforeunload` handles tab close / reload. React Router
  // navigation is intercepted via a click handler on the "Advanced Projects"
  // link (only in-app path leaving this page).
  const processingCount = (session?.outputs || []).filter(o => o.status === 'processing').length;
  useEffect(() => {
    if (processingCount === 0) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = `${processingCount} generation${processingCount > 1 ? 's are' : ' is'} still running. Leave anyway?`;
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [processingCount]);
  const guardedNavigate = (to) => {
    if (processingCount > 0) {
      const ok = window.confirm(
        `⚠️  ${processingCount} generation${processingCount > 1 ? 's are' : ' is'} still running.\n\n` +
        `If you leave now, they will keep generating in the background and appear in your Library when done — ` +
        `but you won't see the live progress on this page.\n\nLeave anyway?`
      );
      if (!ok) return;
    }
    navigate(to);
  };

  const toggle = (id) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const selectAll = () => setSelected(new Set(presets.map(p => p.id)));
  const clearAll = () => setSelected(new Set());

  const totalCredits = useMemo(() => {
    return presets.filter(p => selected.has(p.id)).reduce((sum, p) => sum + (p.credits || 0), 0);
  }, [presets, selected]);

  // 2026-07-25 — Direct Edit preset supports 14 refs (Seedream 4.5 native cap).
  // Standard presets cap at 6.
  const maxRefs = selected.has('direct_edit') ? 14 : 6;

  const onFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = maxRefs - refUrls.length;
    if (remaining <= 0) { toast.error(`Maximum ${maxRefs} references — remove one to add another.`); return; }
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) toast(`Only ${remaining} more allowed — extra files skipped.`, { icon: '⚠️' });
    setUploading(true);
    const fd = new FormData();
    toUpload.forEach(f => fd.append('files', f));
    try {
      const { data } = await axios.post(`${API}/api/koolkonnect/magic/upload-multi`, fd, {
        headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' },
      });
      const items = (data.references || []).map(r => ({
        url: r.url,
        publicUrl: r.public_url,
        display: r.url?.startsWith('http') ? r.url : `${API}${r.url}`,
        filename: r.filename,
      }));
      setRefFiles(prev => [...prev, ...toUpload]);
      setRefUrls(prev => [...prev, ...items]);
      toast.success(items.length > 1 ? `${items.length} references uploaded` : 'Reference uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearReference = () => {
    setRefFiles([]); setRefUrls([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeReference = (idx) => {
    setRefUrls(prev => prev.filter((_, i) => i !== idx));
    setRefFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error('Describe what you want to create.'); return; }
    if (selected.size === 0) { toast.error('Pick at least one output.'); return; }
    // If UGC Recipe is selected, open the composer dialog instead of the
    // standard multi-preset flow (UGC is a chained recipe with its own dialog).
    if (selected.has('ugc_recipe')) {
      setUgcOpen(true);
      return;
    }
    // If Direct Edit is selected, open the Direct Edit composer.
    if (selected.has('direct_edit')) {
      if (refUrls.length === 0) {
        toast.error('Direct Edit needs at least 1 reference image (up to 14).');
        return;
      }
      setDirectOpen(true);
      return;
    }
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/koolkonnect/magic/generate`, {
        prompt: prompt.trim(),
        preset_ids: Array.from(selected),
        reference_urls: refUrls.map(r => r.url),
        brand_hint: brandHint || '',
        identity_mode: identityMode,
      }, auth());
      setSession(data);
      const immediate = (data.outputs || []).filter(o => o.status === 'complete').length;
      const streaming = (data.outputs || []).filter(o => o.status === 'processing').length;
      if (immediate) toast.success(`${immediate} output${immediate > 1 ? 's' : ''} ready`);
      if (streaming) toast(`${streaming} still generating — streaming in`, { icon: '🎬' });
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || 'Generation failed';
      toast.error(String(msg).slice(0, 240));
    } finally {
      setGenerating(false);
    }
  };

  const launchUgc = async () => {
    if (!prompt.trim()) { toast.error('Describe your ad above first.'); return; }
    setUgcSubmitting(true);
    try {
      const caps = ugcCaptions.map(c => c.trim()).filter(Boolean);
      const { data } = await axios.post(`${API}/api/koolkonnect/ugc/generate`, {
        prompt: prompt.trim(),
        orientation: ugcOrient,
        captions: caps,
        auto_captions: ugcAutoCaps,
        reference_urls: refUrls.map(r => r.url),
        brand_hint: brandHint || '',
        identity_mode: identityMode,
      }, auth());
      setSession(data);
      setUgcOpen(false);
      toast.success('UGC recipe running — image → video → music → MP4', { icon: '🎬' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'UGC launch failed');
    } finally {
      setUgcSubmitting(false);
    }
  };

  const launchDirectEdit = async () => {
    if (!prompt.trim()) { toast.error('Describe what you want to change or compose.'); return; }
    if (refUrls.length === 0) { toast.error('At least 1 reference image required.'); return; }
    setDirectSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/koolkonnect/image/direct_edit`, {
        prompt: prompt.trim(),
        intent: directIntent,
        aspect_ratio: directAspect,
        reference_urls: refUrls.map(r => r.url),
      }, auth());
      setSession(data);
      setDirectOpen(false);
      toast.success(`Direct ${directIntent === 'edit' ? 'edit' : 'scene'} complete · zero hallucination`, { icon: '🎨' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Direct Edit failed');
    } finally {
      setDirectSubmitting(false);
    }
  };

  const loadSession = async (sid) => {
    try {
      const { data } = await axios.get(`${API}/api/koolkonnect/magic/session/${sid}`, auth());
      setSession(data);
      if (data.prompt) setPrompt(data.prompt);
      if (data.brand_hint) setBrandHint(data.brand_hint);
      if (Array.isArray(data.reference_urls) && data.reference_urls.length) {
        setRefUrls(data.reference_urls.map(u => ({
          url: u,
          publicUrl: u.startsWith('http') ? u : u,
          display: u.startsWith('http') ? u : `${API}${u}`,
          filename: '',
        })));
      } else if (data.reference_url) {
        setRefUrls([{ url: data.reference_url, publicUrl: data.reference_url, display: data.reference_url.startsWith('http') ? data.reference_url : `${API}${data.reference_url}`, filename: '' }]);
      }
    } catch (_e) { /* ignore */ }
  };

  const regenerateOne = async (presetId) => {
    if (!session?.id) return;
    try {
      const { data } = await axios.post(`${API}/api/koolkonnect/magic/generate`, {
        prompt: session.prompt || prompt.trim(),
        preset_ids: [presetId],
        reference_urls: (session.reference_urls && session.reference_urls.length ? session.reference_urls : refUrls.map(r => r.url)),
        brand_hint: session.brand_hint || brandHint || '',
        identity_mode: identityMode,
        session_id: session.id,
      }, auth());
      setSession(data);
      toast.success('Regenerating…');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Retry failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#050510] text-white" data-testid="koolkonnect-magic">
      {/* Hard-nag banner: Drive is the ONLY way to keep content past 30 days */}
      {!driveStatus.connected && (
        <div className="bg-gradient-to-r from-amber-500/20 via-orange-500/20 to-rose-500/20 border-b border-amber-400/30" data-testid="drive-required-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
            <HardDrive className="h-4 w-4 text-amber-300 shrink-0" />
            <div className="text-[11px] sm:text-xs text-amber-100 flex-1 min-w-0">
              <b className="text-amber-200">Connect Google Drive to keep your work.</b>{' '}
              <span className="hidden sm:inline">Every asset auto-deletes after 30 days unless it's synced to your Drive. </span>
              <span className="sm:hidden">Assets auto-delete after 30 days. </span>
              Drive is your permanent library — free, private, one-click.
            </div>
            <Button
              size="sm"
              onClick={connectDrive}
              className="h-7 px-3 rounded-full bg-amber-400 hover:bg-amber-300 text-black text-[11px] font-semibold shrink-0"
              data-testid="drive-required-connect"
            >
              Connect
            </Button>
          </div>
        </div>
      )}
      {/* Ken-Burns slow zoom for active cover — 32s cycle, no snap-back */}
      <style>{`
        @keyframes kenburns {
          0%   { transform: scale(1.04) translate(0, 0); }
          50%  { transform: scale(1.12) translate(-1%, 0.6%); }
          100% { transform: scale(1.04) translate(0, 0); }
        }
        .kenburns-active { animation: kenburns 32s ease-in-out infinite; will-change: transform; }
      `}</style>      {/* ─── Header ───────────────────────────────────────────────────── */}
      <div className="border-b border-white/5 bg-gradient-to-b from-white/[0.03] to-transparent">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-gradient-to-br from-emerald-400 to-cyan-500 grid place-items-center shadow-lg shadow-emerald-500/30 shrink-0">
              <Sparkles className="h-5 w-5 text-black" />
            </div>
            <div className="min-w-0">
              <div className="text-base sm:text-xl font-semibold leading-tight truncate">KoolKonnect Magic</div>
              <div className="text-[10px] sm:text-xs text-white/50 truncate">One prompt. Every format. Studio quality.</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* In-flight generation indicator — reassures user their work is safe */}
            {processingCount > 0 && (
              <div
                className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-200 text-[11px]"
                title={`${processingCount} generation${processingCount > 1 ? 's' : ''} still running. They'll auto-save to your Library when complete — safe to wait or leave.`}
                data-testid="processing-safe-pill"
              >
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>{processingCount} in progress · auto-saving</span>
              </div>
            )}
            {/* Google Drive autosave pill */}
            {driveStatus.connected ? (
              <div
                className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-200 text-[11px]"
                title={driveStatus.user_email || 'Drive connected'}
                data-testid="drive-connected-pill"
              >
                <HardDrive className="h-3 w-3" />
                <span>Drive on</span>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={connectDrive}
                className="text-white/70 hover:text-white hover:bg-white/5 px-2 sm:px-3"
                data-testid="connect-drive-btn"
              >
                <HardDrive className="h-4 w-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Connect Drive</span>
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => guardedNavigate('/koolkonnect/projects')}
              className="text-white/60 hover:text-white hover:bg-white/5 px-2 sm:px-3"
              data-testid="open-advanced-projects"
            >
              <span className="hidden sm:inline">Advanced Projects </span>
              <span className="sm:hidden">Projects</span>
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-8 grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-5 sm:gap-8 pb-44 md:pb-32 lg:pb-8">
        {/* ─── Left: input panel (order-2 on mobile so tiles are visible first) ─── */}
        <div className="space-y-5 order-2 lg:order-1">
          {/* Reference photos — up to 6 for advanced composition */}
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">
              Reference photos (optional · up to 6)
            </label>
            <div className="grid grid-cols-3 gap-2">
              {refUrls.map((r, idx) => (
                <div key={idx} className="relative rounded-xl overflow-hidden border border-white/10 group aspect-square" data-testid={`magic-ref-${idx}`}>
                  <img src={r.display} alt={`ref-${idx}`} className="w-full h-full object-cover" />
                  <button
                    onClick={() => removeReference(idx)}
                    className="absolute top-1 right-1 p-1 rounded-full bg-black/60 hover:bg-red-500 text-white opacity-0 group-hover:opacity-100 transition"
                    data-testid={`magic-remove-ref-${idx}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                  {idx === 0 && (
                    <div className="absolute bottom-1 left-1 text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/95 text-black font-medium">
                      Primary
                    </div>
                  )}
                </div>
              ))}
              {refUrls.length < maxRefs && (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-white/10 hover:border-emerald-400/50 rounded-xl aspect-square flex items-center justify-center cursor-pointer transition"
                  data-testid="magic-upload-dropzone"
                >
                  {uploading ? (
                    <Loader2 className="h-5 w-5 animate-spin text-white/60" />
                  ) : (
                    <div className="text-center px-2">
                      <Upload className="h-5 w-5 mx-auto mb-1 text-white/40" />
                      <div className="text-[10px] text-white/60 leading-tight">
                        {refUrls.length === 0 ? 'Drop products / people / logos' : 'Add another'}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="mt-2 text-[10px] text-white/40 flex items-center justify-between">
              <span>{refUrls.length}/{maxRefs} · JPG, PNG, WebP up to 10MB each</span>
              {refUrls.length > 0 && (
                <button onClick={clearReference} className="text-white/60 hover:text-white underline" data-testid="magic-clear-references">
                  Clear all
                </button>
              )}
            </div>
            {refUrls.length > 0 && (
              <>
                {/* Identity mode toggle — Preserve (strong lock) vs Restyle (loose inspiration) */}
                <div className="mt-2 rounded-lg border border-white/10 bg-white/5 p-1 flex" data-testid="magic-identity-mode-toggle">
                  <button
                    onClick={() => setIdentityMode('preserve')}
                    className={`flex-1 text-[11px] px-2 py-1.5 rounded-md font-medium transition ${identityMode === 'preserve' ? 'bg-emerald-500 text-black shadow' : 'text-white/60 hover:text-white'}`}
                    data-testid="magic-mode-preserve"
                    title="Lock identity — same face, product, logo"
                  >
                    🔒 Preserve
                  </button>
                  <button
                    onClick={() => setIdentityMode('restyle')}
                    className={`flex-1 text-[11px] px-2 py-1.5 rounded-md font-medium transition ${identityMode === 'restyle' ? 'bg-sky-500 text-white shadow' : 'text-white/60 hover:text-white'}`}
                    data-testid="magic-mode-restyle"
                    title="Use as inspiration — AI can vary the subject"
                  >
                    🎨 Restyle
                  </button>
                </div>
                <div className="mt-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 p-2 text-[10px] text-emerald-200">
                  {identityMode === 'preserve' ? (
                    <>
                      <Check className="h-3 w-3 inline mr-1" />
                      <strong>Preserve mode:</strong> AI keeps the exact face, features, product, and logo. Only what your prompt asks changes.
                      {refUrls.length > 1 && ` All ${refUrls.length} composed together.`}
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3 w-3 inline mr-1" />
                      <strong>Restyle mode:</strong> References are inspiration only. AI can vary subject, wardrobe, and framing freely.
                    </>
                  )}
                </div>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={onFilePick}
              data-testid="magic-upload-input"
            />
          </div>

          {/* Prompt */}
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Describe what you want</label>
            <Textarea
              rows={5}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="A luxury smart ring on a marble surface, morning light streaming through a window, minimalist and premium."
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 focus:border-emerald-400/50 rounded-xl resize-none"
              data-testid="magic-prompt-input"
            />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {['Studio quality', 'Golden hour', 'Editorial magazine', 'Cinematic', 'Neon nightlife'].map(chip => (
                <button key={chip}
                        onClick={() => setPrompt(prompt ? `${prompt}, ${chip}` : chip)}
                        className="text-[10px] px-2 py-1 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white/70">
                  + {chip}
                </button>
              ))}
            </div>
          </div>

          {/* Brand hint */}
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Brand style (optional)</label>
            <Input value={brandHint} onChange={(e) => setBrandHint(e.target.value)}
                   placeholder="e.g. Emerald + gold, minimalist, luxury"
                   className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-xl"
                   data-testid="magic-brand-hint" />
          </div>

          {/* Selection summary + Generate */}
          <div className="pt-2 border-t border-white/10">
            <div className="flex items-center justify-between text-xs text-white/50 mb-3">
              <div><span className="text-white font-semibold">{selected.size}</span> selected · ~{totalCredits} credits</div>
              <div className="flex gap-2">
                <button onClick={selectAll} className="text-white/60 hover:text-white">All</button>
                <span className="text-white/20">·</span>
                <button onClick={clearAll} className="text-white/60 hover:text-white">Clear</button>
              </div>
            </div>
            <Button
              onClick={generate}
              disabled={generating || selected.size === 0 || !prompt.trim()}
              className="w-full h-12 bg-gradient-to-r from-emerald-400 to-cyan-500 hover:from-emerald-500 hover:to-cyan-600 text-black font-medium text-base rounded-xl shadow-lg shadow-emerald-500/20 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="magic-generate-btn"
            >
              {generating ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Casting the spell…</> : <><Wand2 className="h-5 w-5 mr-2" /> Generate {selected.size} output{selected.size !== 1 ? 's' : ''}</>}
            </Button>
          </div>

          {/* Recent sessions — button opens a bottom sheet (saves vertical space) */}
          {history.length > 0 && (
            <button
              onClick={() => setHistoryOpen(true)}
              className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 transition text-sm text-white/80"
              data-testid="magic-history-open"
            >
              <span className="flex items-center gap-2">
                <ChevronLeft className="h-4 w-4 text-white/50" />
                Recent sessions
              </span>
              <Badge className="text-[10px] bg-white/10 text-white/70 border-0">{history.length}</Badge>
            </button>
          )}
        </div>

        {/* ─── Right: preset tiles + results (order-1 on mobile) ─────── */}
        <div className="space-y-6 order-1 lg:order-2">
          {/* Preset grid */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-sm font-semibold">Pick your outputs</div>
                <div className="text-xs text-white/50">One prompt → every format you need.</div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {presets.map((p, idx) => (
                <PresetTile
                  key={p.id}
                  preset={p}
                  index={idx}
                  isOn={selected.has(p.id)}
                  onToggle={() => toggle(p.id)}
                />
              ))}
            </div>
          </div>

          {/* Session outputs */}
          {session && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-sm font-semibold">Your session</div>
                  <div className="text-xs text-white/50 truncate max-w-xl">{session.prompt}</div>
                </div>
                <div className="text-xs text-white/50">
                  {(session.outputs || []).filter(o => o.status === 'complete').length}/{(session.outputs || []).length} ready
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(session.outputs || []).map((out) => (
                  <OutputCard key={out.id} out={out} onRegenerate={regenerateOne} />
                ))}
              </div>
            </div>
          )}

          {!session && (
            <Card className="border-dashed border-white/10 bg-transparent">
              <CardContent className="py-14 text-center text-white/40 text-sm">
                Pick your outputs, describe what you want, then hit <span className="text-emerald-400">Generate</span>.
                <br />
                Everything shows up here.
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Mobile sticky Generate bar — sits ABOVE the app BottomNav (h-16) so it
          never gets covered. Tap the "N selected" chip to expand a peek tray
          showing selected outputs with per-item remove chips. */}
      {selected.size > 0 && (
        <div
          className="fixed left-0 right-0 z-40 lg:hidden bg-[#050510]/95 backdrop-blur-xl border-t border-white/10 shadow-2xl"
          style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom, 0px))' }}
          data-testid="magic-mobile-cta"
        >
          {/* Peek tray — chips of selected outputs, opens on tap of the summary. */}
          {trayOpen && (
            <div className="px-4 pt-3 pb-1 border-b border-white/10 max-h-[40vh] overflow-y-auto" data-testid="magic-mobile-tray">
              <div className="text-[10px] uppercase tracking-widest text-white/50 mb-2">Your selection · tap × to remove</div>
              <div className="flex flex-wrap gap-1.5">
                {Array.from(selected).map(pid => {
                  const p = presets.find(x => x.id === pid);
                  if (!p) return null;
                  return (
                    <button
                      key={pid}
                      onClick={(e) => { e.stopPropagation(); toggle(pid); }}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-100 text-[11px]"
                      data-testid={`magic-tray-chip-${pid}`}
                    >
                      <span>{p.label}</span>
                      <X className="h-3 w-3" />
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="px-4 py-3 flex items-center gap-3">
            <button
              onClick={() => setTrayOpen(v => !v)}
              className="text-[11px] text-white/60 leading-tight text-left flex items-center gap-1"
              data-testid="magic-mobile-summary"
            >
              <span className={`inline-block transition-transform ${trayOpen ? 'rotate-180' : ''}`}>▲</span>
              <span>
                <span className="text-white font-medium text-xs block">{selected.size} selected</span>
                <span>~{totalCredits} credits</span>
              </span>
            </button>
            <Button
              onClick={generate}
              disabled={generating || !prompt.trim()}
              className="flex-1 h-11 bg-gradient-to-r from-emerald-400 to-cyan-500 hover:from-emerald-500 hover:to-cyan-600 text-black font-medium rounded-xl disabled:opacity-40"
              data-testid="magic-generate-btn-mobile"
            >
              {generating ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Casting…</> : <><Wand2 className="h-4 w-4 mr-1.5" /> Generate</>}
            </Button>
          </div>
          {!prompt.trim() && (
            <div className="text-[10px] text-amber-300/90 pb-2 text-center">Add a prompt above ↑ to enable Generate</div>
          )}
        </div>
      )}

      {/* Direct Edit Composer — zero-hallucination edit/compose from up to 14 refs */}
      <Sheet open={directOpen} onOpenChange={setDirectOpen}>
        <SheetContent side="bottom" className="bg-[#0a0a1a] border-white/10 text-white max-h-[92vh] overflow-y-auto" data-testid="direct-edit-composer">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-fuchsia-300" />
              Direct Edit · Zero Hallucination
            </SheetTitle>
            <SheetDescription className="text-white/60">
              Pixel-faithful edit or scene composition from your uploaded references (up to 14). Powered by Kaharian Hub.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-5 space-y-5">
            {/* Intent */}
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">What do you want to do?</div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'edit',    label: 'Edit / Refine',   sub: 'Adjust ONE primary reference. Preserve identity 100%.' },
                  { id: 'compose', label: 'Compose Scene',   sub: 'Build a new scene from 2-14 references. No hallucinations.' },
                ].map(o => (
                  <button
                    key={o.id}
                    onClick={() => setDirectIntent(o.id)}
                    className={`rounded-xl border p-3 text-left transition ${directIntent === o.id ? 'border-fuchsia-400 bg-fuchsia-400/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
                    data-testid={`direct-intent-${o.id}`}
                  >
                    <div className={`text-sm font-medium ${directIntent === o.id ? 'text-fuchsia-200' : 'text-white'}`}>{o.label}</div>
                    <div className="text-[10px] text-white/60 mt-1">{o.sub}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Aspect */}
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Aspect Ratio</div>
              <div className="flex flex-wrap gap-2">
                {['1:1', '4:3', '3:4', '16:9', '9:16', '3:2', '2:3'].map(a => (
                  <button
                    key={a}
                    onClick={() => setDirectAspect(a)}
                    className={`px-3 py-1.5 rounded-full text-xs border transition ${directAspect === a ? 'border-fuchsia-400 bg-fuchsia-400/10 text-fuchsia-200' : 'border-white/10 bg-white/5 text-white/70 hover:bg-white/10'}`}
                    data-testid={`direct-aspect-${a}`}
                  >
                    {a}
                  </button>
                ))}
              </div>
            </div>

            {/* Refs summary */}
            <div className="rounded-xl bg-fuchsia-500/10 border border-fuchsia-500/20 p-3 text-[11px] text-fuchsia-100">
              <div className="flex items-center justify-between">
                <span>{refUrls.length} reference{refUrls.length !== 1 ? 's' : ''} · max 14 · ~12 credits</span>
                <span className="text-fuchsia-300 font-semibold">Kaharian Hub · Direct Edit</span>
              </div>
              <div className="mt-1.5 text-[10px] text-fuchsia-200/80">
                Model is auto-instructed to preserve every subject, face, product, logo, and typography <b>exactly as shown</b> — no reinterpretation.
              </div>
            </div>

            {/* CTA */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => setDirectOpen(false)}
                className="text-white/70 hover:text-white"
                data-testid="direct-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={launchDirectEdit}
                disabled={directSubmitting || !prompt.trim() || refUrls.length === 0}
                className="flex-1 h-11 bg-gradient-to-r from-fuchsia-400 to-pink-500 hover:from-fuchsia-500 hover:to-pink-600 text-black font-medium rounded-xl disabled:opacity-40"
                data-testid="direct-launch"
              >
                {directSubmitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</> : <><Wand2 className="h-4 w-4 mr-2" /> Cast Direct {directIntent === 'edit' ? 'Edit' : 'Scene'}</>}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* UGC Composer — appears when the "UGC Video Ad Recipe" tile is picked. */}
      <Sheet open={ugcOpen} onOpenChange={setUgcOpen}>
        <SheetContent side="bottom" className="bg-[#0a0a1a] border-white/10 text-white max-h-[92vh] overflow-y-auto" data-testid="ugc-composer">
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <Film className="h-5 w-5 text-emerald-300" />
              UGC Video Ad · Compose
            </SheetTitle>
            <SheetDescription className="text-white/60">
              One click chains: hero image → 5s video → music bed → captions → burned-in MP4. Ready for TikTok / Reels / Shorts.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-5 space-y-5">
            {/* Orientation */}
            <div>
              <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Format</div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'vertical',   label: 'Vertical', sub: '9:16 · 15s · TikTok / Reels', ratio: 'aspect-[9/16]' },
                  { id: 'horizontal', label: 'Horizontal', sub: '16:9 · 30s · YouTube / LinkedIn', ratio: 'aspect-video' },
                ].map(o => (
                  <button
                    key={o.id}
                    onClick={() => setUgcOrient(o.id)}
                    className={`rounded-xl border p-3 text-left transition ${ugcOrient === o.id ? 'border-emerald-400 bg-emerald-400/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}
                    data-testid={`ugc-orient-${o.id}`}
                  >
                    <div className={`${o.ratio} rounded-md bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-white/10 mb-2 grid place-items-center`}>
                      <Film className={`${ugcOrient === o.id ? 'text-emerald-300' : 'text-white/40'} h-6 w-6`} />
                    </div>
                    <div className="text-sm font-medium">{o.label}</div>
                    <div className="text-[10px] text-white/60">{o.sub}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Captions */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-white/50">Captions</div>
                <label className="flex items-center gap-1.5 text-[11px] text-white/70 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ugcAutoCaps}
                    onChange={(e) => setUgcAutoCaps(e.target.checked)}
                    className="accent-emerald-400"
                    data-testid="ugc-auto-caps"
                  />
                  Auto-suggest missing lines
                </label>
              </div>
              <div className="space-y-1.5">
                {ugcCaptions.slice(0, ugcOrient === 'vertical' ? 3 : 4).map((c, i) => (
                  <Input
                    key={i}
                    value={c}
                    onChange={(e) => {
                      const next = [...ugcCaptions];
                      next[i] = e.target.value;
                      setUgcCaptions(next);
                    }}
                    placeholder={`Caption ${i + 1} (leave blank to auto-generate)`}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 rounded-lg text-sm"
                    maxLength={120}
                    data-testid={`ugc-caption-${i}`}
                  />
                ))}
              </div>
              <div className="mt-1.5 text-[10px] text-white/40">
                Short punchy lines burn onto the video. Under 6 words each is ideal.
              </div>
            </div>

            {/* Cost + Drive hint */}
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-3 text-[11px] text-emerald-100">
              <div className="flex items-center justify-between">
                <span>Chained recipe · ~240 credits</span>
                <span className="text-emerald-300 font-semibold">1 click</span>
              </div>
              {driveStatus.connected ? (
                <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-emerald-200">
                  <HardDrive className="h-3 w-3" />
                  Your final MP4 will auto-save to Google Drive → <b>Konnect / KoolKonnect / ugc</b>.
                </div>
              ) : (
                <button onClick={connectDrive} className="mt-1.5 text-[10px] underline text-emerald-200 hover:text-emerald-100">
                  Connect Google Drive to autosave your final MP4 (bypass 30-day retention)
                </button>
              )}
            </div>

            {/* CTA */}
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={() => setUgcOpen(false)}
                className="text-white/70 hover:text-white"
                data-testid="ugc-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={launchUgc}
                disabled={ugcSubmitting || !prompt.trim()}
                className="flex-1 h-11 bg-gradient-to-r from-emerald-400 to-cyan-500 hover:from-emerald-500 hover:to-cyan-600 text-black font-medium rounded-xl disabled:opacity-40"
                data-testid="ugc-launch"
              >
                {ugcSubmitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Kicking off recipe…</> : <><Wand2 className="h-4 w-4 mr-2" /> Cast UGC recipe</>}
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Kaharian Hub live-version pill — enterprise social proof, footer */}
      <div className="mt-8 mb-4 flex justify-center" data-testid="kaharian-hub-pill-wrap">
        <a
          href="https://kaharian.com"
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 text-[10px] text-white/50 hover:text-white/80 transition"
          data-testid="kaharian-hub-pill"
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60"></span>
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
          </span>
          <span>Powered by</span>
          <span className="font-semibold text-white/80 group-hover:text-white">Kaharian Hub</span>
          {hubVersion && <span className="text-emerald-300/80 tabular-nums">v{hubVersion}</span>}
        </a>
      </div>

      {/* Recent Sessions — bottom sheet on mobile, side sheet on desktop */}
      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent side="bottom" className="bg-[#0a0a1a] border-white/10 text-white max-h-[75vh]" data-testid="magic-history-sheet">
          <SheetHeader>
            <SheetTitle className="text-white">Recent sessions</SheetTitle>
            <SheetDescription className="text-white/50">Reopen any session to see its outputs or regenerate.</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-1.5 overflow-y-auto max-h-[55vh]">
            {history.map(h => (
              <button
                key={h.id}
                onClick={() => { loadSession(h.id); setHistoryOpen(false); }}
                className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-white/5 transition text-sm text-white/85 flex items-center gap-2"
                data-testid={`magic-history-${h.id}`}
              >
                <ChevronLeft className="h-3 w-3 text-white/40 shrink-0" />
                <span className="truncate flex-1">{h.prompt || 'Untitled'}</span>
                <Badge className="text-[10px] bg-white/10 text-white/60 border-0 shrink-0">
                  {(h.outputs || []).length}
                </Badge>
              </button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

// ─── Output card ─────────────────────────────────────────────────────────────
function OutputCard({ out, onRegenerate }) {
  const navigate = useNavigate();
  const isImg = out.kind === 'image';
  const isVid = out.kind === 'video' || out.kind === 'ugc';
  const isMus = out.kind === 'music';
  const isTxt = out.kind === 'text';
  const isUgc = out.kind === 'ugc';
  const done = out.status === 'complete';
  const failed = out.status === 'failed';
  const processing = out.status === 'processing';
  // Kaharian assets stored locally return relative /api/uploads URLs — prepend API host.
  const displayUrl = out.url && !out.url.startsWith('http') ? `${API}${out.url}` : out.url;

  const download = async () => {
    if (!displayUrl) return;
    try {
      const res = await fetch(displayUrl, { mode: 'cors' });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${out.label.replace(/\s+/g, '-').toLowerCase()}.${isImg ? 'png' : isVid ? 'mp4' : isMus ? 'mp3' : 'md'}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_e) {
      window.open(displayUrl, '_blank');
    }
  };

  // UGC-only: pretty stage label.
  const UGC_STAGES = {
    image: 'Composing hero image…',
    video: 'Rendering 5-second video…',
    music: 'Producing music bed…',
    captions: 'Writing caption cards…',
    assemble: 'Assembling MP4 with captions…',
    done: 'Ready',
    failed: 'Failed',
  };

  return (
    <div className="rounded-2xl overflow-hidden border border-white/10 bg-white/[0.02] group" data-testid={`magic-output-${out.preset_id}`}>
      <div className="relative aspect-[4/3] bg-black/40 grid place-items-center overflow-hidden">
        {processing && (
          <>
            <GenerationProgress
              kind={out.kind}
              startedAt={out.started_at || out.created_at}
              complete={false}
              failed={false}
              variant="full"
            />
            {isUgc && (
              <div className="absolute bottom-2 left-2 right-2 px-3 py-1.5 rounded-lg bg-black/70 backdrop-blur text-[11px] text-emerald-200 border border-emerald-500/30" data-testid="ugc-stage-label">
                <div className="flex items-center gap-1.5">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  <span>{UGC_STAGES[out.stage] || 'Working…'}</span>
                </div>
              </div>
            )}
          </>
        )}
        {failed && (
          <div className="text-center px-4">
            <X className="h-6 w-6 text-red-400 mx-auto mb-2" />
            <div className="text-xs text-white/80">Couldn&apos;t generate</div>
            <div className="text-[10px] text-white/40 mt-1 line-clamp-2">{out.error}</div>
            {onRegenerate && (
              <button
                onClick={() => onRegenerate(out.preset_id)}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] bg-emerald-400 hover:bg-emerald-300 text-black font-medium"
                data-testid={`magic-retry-${out.preset_id}`}
              >
                <RefreshCw className="h-3 w-3" /> Regenerate
              </button>
            )}
          </div>
        )}
        {done && isImg && <img src={displayUrl} alt={out.label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
        {done && (out.kind === 'video' || out.kind === 'ugc') && (
          <video src={displayUrl} controls className="w-full h-full object-cover" preload="metadata" />
        )}
        {done && isTxt && (
          <div className="w-full h-full p-4 overflow-y-auto text-white/85 text-[11px] leading-relaxed bg-gradient-to-br from-slate-900 to-slate-950">
            <div className="max-w-none whitespace-pre-wrap font-serif">
              {(out.text || '').split('\n').slice(0, 20).join('\n')}
              {out.text && out.text.split('\n').length > 20 && <div className="text-white/40 text-[10px] mt-2">…({out.text.length.toLocaleString()} characters — download to read all)</div>}
            </div>
          </div>
        )}
        {done && isMus && (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-500/20 to-cyan-500/20">
            <div className="text-center w-full px-4">
              <Music className="h-8 w-8 mx-auto text-emerald-300 mb-3" />
              <audio src={displayUrl} controls className="w-full" />
            </div>
          </div>
        )}
      </div>
      <div className="p-3 flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{out.label}</div>
          <div className="text-[10px] text-white/40">
            {done ? (isMus || isVid || isImg ? 'Saved to Library ✓' : 'Ready') : failed ? 'Failed' : 'Processing'}
          </div>
        </div>
        {done && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => {
                const libKind = out.kind === 'ugc' ? 'video' : out.kind;
                navigate(`/koolkonnect/projects?tab=library&kind=${libKind}`);
                toast.success('Opened in Library');
              }}
              className="p-1.5 rounded-md text-white/70 hover:text-emerald-300 hover:bg-white/10 transition"
              title="Open in Library"
              data-testid={`magic-open-library-${out.preset_id}`}
            >
              <FolderOpen className="h-4 w-4" />
            </button>
            <Button size="sm" variant="ghost" onClick={download}
                    className="text-white/70 hover:text-white hover:bg-white/10"
                    data-testid={`magic-download-${out.preset_id}`}>
              <Download className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
